import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRegEyeSlash } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { IoChevronBackOutline } from 'react-icons/io5';
import { GoEye } from 'react-icons/go';
import { FaCheckCircle } from 'react-icons/fa';
import {
	requestAuthLogin,
	requestUserDetails,
	requestResetPassword,
	requestNewPassword,
} from '../services/requests';
import NavBar from '../components/NavBar';
import Cookies from 'js-cookie';

const normalizePermissionList = (...sources) => [
	...new Set(
		sources
			.flatMap((source) =>
				Array.isArray(source)
					? source
					: source === undefined || source === null
						? []
						: [source],
			)
			.map((permission) => String(permission || '').trim())
			.filter(Boolean),
	),
];

const hasNamedPermission = (permissions = [], permissionName = '') => {
	const target = String(permissionName || '')
		.trim()
		.toLowerCase();
	if (!target) return false;

	return normalizePermissionList(permissions).some(
		(permission) => String(permission).toLowerCase() === target,
	);
};

const resolveUserCookiePermissions = ({
	userDetails,
	unitProfile = null,
	basePermissions = [],
}) => {
	const permissions = normalizePermissionList(
		basePermissions,
		unitProfile?.groups,
		userDetails?.permissao,
		userDetails?.permissions,
		userDetails?.groups,
		userDetails?.grupos,
	);

	return hasNamedPermission(permissions, 'EscalaMedicaEdicao')
		? normalizePermissionList(permissions, 'EscalaMedicaEdicao')
		: permissions;
};

const buildUserCookiePayload = ({
	userDetails,
	loginToken,
	cargo,
	unidade,
	unitProfile = null,
	basePermissions = [],
	id,
	cnes,
}) => ({
	nome: userDetails.nome,
	userName: userDetails.username,
	username: userDetails.username,
	email: userDetails.email,
	cargo,
	permissao: resolveUserCookiePermissions({
		userDetails,
		unitProfile,
		basePermissions,
	}),
	unidade,
	...(id !== undefined ? { id } : {}),
	...(cnes !== undefined ? { cnes } : {}),
	token: loginToken,
});

function Login() {
	const [input, setInput] = useState('');
	const [password, setPassword] = useState('');

	const [inputCode, setInputCode] = useState('');

	const [showAlert, setShowAlert] = useState('');
	const [showCPFAlert, setCPFAlert] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [cardAnimation, setCardAnimation] = useState(false);
	const [showPassword2, setShowPassword2] = useState(false);
	const [showUnit, setShowUnit] = useState(false);
	const [codeError, setCodeError] = useState([]);
	const [newPasswordCreated, setNewPasswordCreated] = useState(false);
	const [recPage, setRecPage] = useState(false);
	const [recStatus, setRecStatus] = useState('static');
	const [newPassword, setNewPassword] = useState('');
	const [newPassword2, setNewPassword2] = useState('');
	const [recEmail, setRecEmail] = useState('');
	const [unitSelected, setUnitSelected] = useState(false);
	const [loading, setLoading] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [inputValue, setInputValue] = useState({ unidade: '', cnes: '' });
	const [filteredList, setFilteredList] = useState([]);
	const [error, setError] = useState(false);
	const navigate = useNavigate();

	const handleNewPassword = async () => {
		const data = await requestNewPassword({
			email: recEmail,
			code: inputCode,
			new_password: newPassword,
			confirm_password: newPassword2,
		});
		if (data.error) {
			setCodeError(Object.values(data.error));
		} else {
			setNewPasswordCreated(true);
			setTimeout(() => {
				setCardAnimation(true);
			}, 100);
		}
	};
	const handleSendEmail = async () => {
		setButtonLoading(true);
		const data = await requestResetPassword({ email: recEmail });
		if (data.error) {
			setError(true);
			setButtonLoading(false);
		} else {
			setButtonLoading(false);
			setError(false);
			setRecStatus('Email enviado!');
		}
	};

	const handleLogin = useCallback(
		async (event) => {
			let loginAcces = await requestAuthLogin({
				username: input,
				password: password,
			});

			if (!loginAcces.error) {
				let userDetails = await requestUserDetails(
					loginAcces.token,
					input.replace(/[-.]/g, ''),
				);
				if (!userDetails.error) {
					const developerBasePermissions = [
						'Investigadores',
						'Classificadores',
					];
					if (
						userDetails.is_subhue === true ||
						userDetails.smsrio === true
					) {
						if (userDetails.desenvolvedor === true) {
							Cookies.set(
								'user',
								JSON.stringify(
									buildUserCookiePayload({
										userDetails,
										loginToken: loginAcces.token,
										cargo: 'desenvolvedor',
										unidade: 'subhue',
										basePermissions:
											developerBasePermissions,
									}),
								),
								{ expires: 2 },
							);
							navigate('/hub');
						} else {
							Cookies.set(
								'user',
								JSON.stringify(
									buildUserCookiePayload({
										userDetails,
										loginToken: loginAcces.token,
										cargo:
											userDetails.smsrio === true
												? 'sms'
												: 'usuario',
										unidade:
											userDetails.smsrio === true
												? 'sms'
												: 'subhue',
									}),
								),
								{ expires: 2 },
							);
							navigate('/hub');
						}
					} else if (userDetails.perfil_unidades.length === 1) {
						const unidade = userDetails.perfil_unidades[0];
						Cookies.set(
							'user',
							JSON.stringify(
								buildUserCookiePayload({
									userDetails,
									loginToken: loginAcces.token,
									cargo: unidade.groups.includes('Gestores')
										? userDetails.smsrio === true
											? 'sms'
											: 'gestor'
										: userDetails.smsrio === true
											? 'sms'
											: 'usuario',
									unidade: unidade.nome_unidade,
									unitProfile: unidade,
									id: unidade.unidade_id,
									cnes: unidade.cnes,
								}),
							),
							{ expires: 2 },
						);
						setUnitSelected(true);
						navigate('/hub');
					} else if (userDetails?.perfil_unidades?.length > 1) {
						if (!unitSelected) {
							const activeUnities =
								userDetails.perfil_unidades.filter(
									(item) => item.ativo === true,
								);
							if (activeUnities.length === 0) {
								// Se não houver unidades ativas, cria perfil padrão
								Cookies.set(
									'user',
									JSON.stringify(
										buildUserCookiePayload({
											userDetails,
											loginToken: loginAcces.token,
											cargo:
												userDetails.smsrio === true
													? 'sms'
													: userDetails.is_subhue ===
														  true
														? 'suhue'
														: 'usuario',
											unidade:
												userDetails.smsrio === true
													? 'sms'
													: userDetails.is_subhue ===
														  true
														? 'suhue'
														: '',
										}),
									),
									{ expires: 2 },
								);
								setUnitSelected(true);
								navigate('/hub');
							} else {
								setFilteredList(activeUnities);
								setShowUnit(true);
							}
						} else {
							const selectedUnit =
								userDetails.perfil_unidades.find(
									(unidade) =>
										unidade?.nome_unidade ===
										inputValue?.unidade,
								);
							Cookies.set(
								'user',
								JSON.stringify(
									buildUserCookiePayload({
										userDetails,
										loginToken: loginAcces?.token,
										cargo: selectedUnit.groups.includes(
											'Gestores',
										)
											? userDetails.smsrio === true
												? 'sms'
												: 'gestor'
											: userDetails.smsrio === true
												? 'sms'
												: 'usuario',
										unidade: inputValue?.unidade,
										unitProfile: selectedUnit,
										id: selectedUnit.unidade_id,
										cnes: inputValue?.cnes,
									}),
								),
								{ expires: 2 },
							);
							navigate('/hub');
						}
					} else if (userDetails.perfil_unidades.length === 0) {
						// Nova condição para perfil sem unidades
						Cookies.set(
							'user',
							JSON.stringify(
								buildUserCookiePayload({
									userDetails,
									loginToken: loginAcces.token,
									cargo:
										userDetails.smsrio === true
											? 'sms'
											: userDetails.is_subhue === true
												? 'suhue'
												: 'usuario',
									unidade:
										userDetails.smsrio === true
											? 'sms'
											: userDetails.is_subhue === true
												? 'suhue'
												: '',
								}),
							),
							{ expires: 2 },
						);
						setUnitSelected(true);
						navigate('/hub');
					} else if (userDetails.is_subhue) {
						Cookies.set(
							'user',
							JSON.stringify(
								buildUserCookiePayload({
									userDetails,
									loginToken: loginAcces.token,
									cargo:
										userDetails.smsrio === true
											? 'sms'
											: 'usuario',
									unidade:
										userDetails.smsrio === true
											? 'sms'
											: 'subhue',
								}),
							),
							{ expires: 2 },
						);
						setUnitSelected(true);
						navigate('/hub');
					}
				} else {
					userDetails.error === '400'
						? setCPFAlert(true)
						: setShowAlert(loginAcces.error);
				}
			} else {
				if (Object.values(loginAcces.error)[0][0].includes('Este')) {
					if (Object.keys(loginAcces.error)[0] === 'username') {
						setShowAlert('O campo de CPF está vazio ');
					} else {
						setShowAlert('O campo de senha está vazio ');
					}
				} else {
					setShowAlert(Object.values(loginAcces.error)[0][0]);
				}
			}
		},
		[
			input,
			password,
			inputValue,
			unitSelected,
			setShowAlert,
			setCPFAlert,
			setFilteredList,
			setShowUnit,
			setUnitSelected,
			navigate,
		],
	);

	const handleSelectItem = (event) => {
		const [nome_unidade, cnes] = event.target.value.split(' - ');
		setInputValue({ unidade: nome_unidade, cnes: cnes });
		setUnitSelected(true);
	};

	useEffect(() => {
		function handleKeyDown(event) {
			if (
				event.key === 'Enter' &&
				!(
					unitSelected === false &&
					inputValue.unidade === '' &&
					showUnit === true
				)
			) {
				handleLogin();
			}
		}

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [unitSelected, inputValue.unidade, showUnit, handleLogin]);

	return (
		<div className='flex flex-col min-h-screen font-inter'>
			<NavBar />
			<div className='text-center flex-grow my-10'>
				{newPasswordCreated && (
					<div className='fixed flex w-full h-full z-10 opacity-60 bg-black'></div>
				)}
				{loading ? (
					<div className='loader'>
						<div
							className='flex justify-center h-[400px] content-center align-bottom items-baseline  w-full my-20'
							role='status'>
							<svg
								aria-hidden='true'
								className='w-8 h-8 text-gray-200 animate-spin mt-[100px]  fill-blue-600'
								viewBox='0 0 100 101'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'>
								<path
									d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
									fill='currentColor'
								/>
								<path
									d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
									fill='currentFill'
								/>
							</svg>
							<span className='sr-only'>Loading...</span>
						</div>
					</div>
				) : (
					<section className={`${!recPage ? '' : ' h-auto'}`}>
						{newPasswordCreated && (
							<div
								className={`fixed rounded-md top-2/4 left-1/2 -translate-x-1/2 -translate-y-3/4 z-20 transition-all duration-500 bg-white flex flex-col items-center overflow-hidden ${
									cardAnimation
										? 'w-[60%] lg:w-[36%] xl:w-[25%] 2xl:w-[22%] h-[130px] opacity-100 scale-100'
										: 'w-0 h-0 opacity-0 scale-0'
								}`}>
								<div className='flex w-full bg-[#E3E3E3] justify-end h-[25px] rounded-t-md '></div>
								<div
									className={`flex items-center h-full justify-center transition-all duration-500 gap-4 p-6 overflow-hidden 
												w-full`}>
									<div className='text-[#32993E] content-center transition-all duration-500'>
										<FaCheckCircle className='scale-[180%] transition-all duration-500' />
									</div>
									<p className='text-[#757575] font-inter content-center text-[24px] text-center font-semibold transition-all duration-500'>
										Nova Senha criada
									</p>
								</div>
								<button
									className='text-sm font-medium hover:text-[#00C0F3] text-[#616161] transition-all duration-200 mb-2'
									onClick={() => {
										setLoading(true);
										setNewPasswordCreated(false);
										setTimeout(() => {
											setLoading(false);
										}, 1000);
										setRecPage(false);
										setRecStatus('static');
									}}>
									{' '}
									Voltar a tela de login
								</button>
							</div>
						)}
						<div className='flex flex-row justify-around  sm:w-[60%]  items-center mt-24 mx-auto md:h-full lg:py-0'>
							{!recPage ? (
								<div className='md:w-full bg-white rounded-lg shadow border md:mt-0 sm:max-w-md transition-all duration-400 xl:p-0'>
									<div className='p-6  space-y-4 md:space-y-10 sm:py-[32px] sm:px-[18px]'>
										<h1 className='text-xl font-bold leading-tight tracking-tight text-[#004A80] md:text-[32px]'>
											Login
										</h1>
										<form
											className='space-y-[18px] mt-[32px] '
											/* onSubmit={handleLogin} */
										>
											<div>
												<input
													value={input}
													onChange={({
														target: { value },
													}) => setInput(value)}
													name='input'
													id='input'
													className=' border sm:text-sm text-[#838383]
													placeholder:text-[#E3E3E3] h-[40px] border-[#E3E3E3] rounded-md block w-full  mx-auto p-2.5'
													placeholder='CPF'
													required
												/>
											</div>
											<div className='relative'>
												<input
													value={password}
													onChange={({
														target: { value },
													}) => setPassword(value)}
													type={
														showPassword
															? 'text'
															: 'password'
													}
													name='password'
													id='password'
													placeholder='Senha'
													autoComplete='true'
													className='text-[#838383] border rounded-md
													placeholder:text-[#E3E3E3] border-[#E3E3E3] sm:text-sm focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5'
													required
												/>
												<div>
													<p
														className={`font-inter font-bold text-[#004A80] my-2 mt-6 text-sm ${
															showUnit
																? ''
																: 'hidden'
														}`}>
														SELECIONE A UNIDADE
													</p>

													{filteredList?.length >
													0 ? (
														<select
															onChange={
																handleSelectItem
															}
															className={` border  text-gray-500 sm:text-sm mb-6 focus:ring-primary-600 focus:border-primary-600 rounded-md block w-full p-2.5 ${
																showUnit
																	? ''
																	: 'hidden'
															}`}>
															<option value=''>
																Escolha a
																unidade
															</option>

															{filteredList.map(
																(
																	item,
																	index,
																) => (
																	<option
																		className=''
																		value={`${item.nome_unidade} - ${item.cnes}`}
																		id={
																			item.cnes
																		}
																		key={
																			index
																		}>
																		{
																			item.nome_unidade
																		}
																	</option>
																),
															)}
														</select>
													) : null}
												</div>
												<button
													type='button'
													onClick={() =>
														setShowPassword(
															!showPassword,
														)
													}
													className={`absolute   right-0 pr-3  flex items-center text-gray-500 ${
														showUnit
															? 'mt-[-145px]'
															: 'mt-[-30px]'
													}`}>
													{showPassword ? (
														<GoEye className='scale-110' />
													) : (
														<FaRegEyeSlash className='scale-110' />
													)}
												</button>
												<div className='flex items-center mt-7 justify-between'>
													<div className='flex items-start'>
														<div className='flex items-center h-5'>
															<input
																id='remember'
																type='checkbox'
																className='sm:w-4 sm:h-4 w-3 h-3 border opacity-50 border-gray-300 bg-gray-50 focus:ring-3 focus:ring-[#757575]'
															/>
														</div>
														<div className='ml-2 text-[10px] sm:text-[14px] text-[#757575]'>
															<label htmlFor='remember'>
																Lembrar-me
															</label>
														</div>
													</div>
													<button
														type='button'
														onClick={() => {
															setLoading(true);
															setTimeout(() => {
																setLoading(
																	false,
																);
															}, 1000);
															setRecStatus(
																'static',
															);
															setRecPage(true);
														}}
														className='text-[10px] sm:text-[14px] font-normal text-[#757575] hover:underline'>
														Esqueceu sua senha?
													</button>
												</div>
												<div className='flex flex-col gap-6 mt-8 mb-2 '>
													<button
														type='button'
														disabled={
															unitSelected ===
																false &&
															inputValue.unidade ===
																'' &&
															showUnit === true
																? true
																: false
														}
														className='text-white bg-[#aaaaaa] enabled:bg-[#004A80] enabled:hover:bg-[#13335A] focus:ring-4 focus:ring-blue-300 font-normal text-[15px] w-[50%] rounded-[4px] transition-all duration-300 px-5 py-2  focus:outline-none mx-auto shadow-md'
														onClick={handleLogin}>
														Entrar
													</button>
													<div className='w-[100%] h-[1px] bg-[#E3E3E3] mx-auto'></div>
													<div className='w-full'>
														<button
															type='button'
															onClick={() => {
																navigate(
																	'/register',
																);
															}}
															className='font-normal p-2 border-[1px]  border-[#E3E3E3]  hover:bg-[#F1F1F1] hover:text-[#757575] hover:border-transparent text-[#757575]  w-[50%] rounded-[4px] sm:px-5 px-3 py-2 '>
															Criar Login
														</button>
													</div>
												</div>
											</div>

											{showAlert?.length > 0 && (
												<p className='text-red-500 font-roboto font-semibold text-sm'>
													{showAlert.slice(0, -1)}
												</p>
											)}
											{showCPFAlert && (
												<p className='text-red-500 font-roboto text-sm font-semibold'>
													Usuário ou Senha Incorretos
												</p>
											)}
										</form>
									</div>
								</div>
							) : (
								<>
									{/* REDEFINIÇÃO DE SENHA */}
									<div
										className={`md:w-[60%] h-full bg-white w-[80%] sm:w-full rounded-lg shadow border md:mt-0 ${
											recStatus === 'Email enviado!'
												? 'sm:max-w-[500px]'
												: 'sm:max-w-[450px] '
										} xl:p-0`}>
										<div className='p-6 space-y-4 md:space-y-4 sm:py-8 sm:px-4'>
											<h1 className='text-xl text-start font-bold leading-tight tracking-tight text-[#004A80] md:text-3xl mb-10'>
												Redefinir Senha
											</h1>
											{recStatus === 'Email enviado!' ? (
												<div className='flex flex-col '>
													<p className='text-start font-inter text-[14px] font-semibold text-gray-500'>
														{' '}
														Foi enviada uma mensagem
														para o email que você
														forneceu contendo o
														código de autenticação.
													</p>

													<p className='text-start font-inter text-[14px] font-normal text-gray-500 my-4'>
														Verifique sua caixa de
														email e digite o código
														fornecido abaixo.
													</p>
												</div>
											) : (
												<p className='text-start font-inter text-[14px] font-medium text-gray-500 '>
													{' '}
													Informe o e-mail da sua
													conta para receber o código
													de recuperação.
												</p>
											)}
											<form className='space-y-4 md:space-y-6'>
												<div className=''>
													{recStatus ===
													'Email enviado!' ? (
														<div className='flex flex-col gap-7'>
															<div className='flex flex-col gap-2'>
																<p className='text-[#004A80] font-semibold text-start font-inter text-[14px]'>
																	Escolha uma
																	nova senha
																</p>
																<div className='relative w-[60%]'>
																	<input
																		value={
																			newPassword
																		}
																		type={
																			showPassword
																				? 'text'
																				: 'password'
																		}
																		onChange={({
																			target: {
																				value,
																			},
																		}) =>
																			setNewPassword(
																				value,
																			)
																		}
																		name='newPassword'
																		id='newPassword'
																		className=' border border-gray-300 text-gray-400 sm:text-sm placeholder:text-gray-300 w-[100%] focus:ring-primary-600 focus:border-primary-600 block rounded-md  p-2.5'
																		placeholder='Nova Senha'
																		required
																	/>
																	<button
																		type='button'
																		onClick={() =>
																			setShowPassword(
																				!showPassword,
																			)
																		}
																		className='absolute bottom-[12px] right-[12px] text-gray-500'>
																		{!showPassword ? (
																			<FaEyeSlash />
																		) : (
																			<FaEye />
																		)}
																	</button>
																</div>
																<div className='relative w-[60%]'>
																	<input
																		type={
																			showPassword2
																				? 'text'
																				: 'password'
																		}
																		value={
																			newPassword2
																		}
																		onChange={({
																			target: {
																				value,
																			},
																		}) =>
																			setNewPassword2(
																				value,
																			)
																		}
																		name='newPassword2'
																		id='newPassword2'
																		className='0 border border-gray-300 text-gray-400 placeholder:text-gray-300 sm:text-sm w-[100%] focus:ring-primary-600 focus:border-primary-600 block rounded-md  p-2.5'
																		placeholder='Confirme a Senha'
																		required
																	/>
																	<button
																		type='button'
																		onClick={() =>
																			setShowPassword2(
																				!showPassword2,
																			)
																		}
																		className='absolute bottom-[12px] right-[12px] text-gray-500'>
																		{!showPassword2 ? (
																			<FaEyeSlash />
																		) : (
																			<FaEye />
																		)}
																	</button>
																</div>
																{newPassword.length >=
																	6 &&
																	newPassword2.length >
																		0 &&
																	newPassword2 !==
																		newPassword && (
																		<div className='text-red-600 text-start text-[11px]'>
																			senhas
																			não
																			coincidem
																		</div>
																	)}
															</div>

															<div className='flex flex-col gap-2 w-[60%]'>
																<p className='text-[#004A80] font-semibold text-start font-inter text-[14px]'>
																	Digite o
																	código
																</p>
																<input
																	type='text'
																	value={
																		inputCode
																	}
																	onChange={({
																		target: {
																			value,
																		},
																	}) =>
																		setInputCode(
																			value,
																		)
																	}
																	className=' border border-gray-300 text-gray-700 sm:text-sm w-[100%] focus:ring-primary-600  placeholder:text-gray-300 focus:border-primary-600 block rounded-md  p-2.5'
																	placeholder='Código de Recuperação'
																	required
																/>
																{codeError?.length >
																	0 &&
																	codeError.map(
																		(
																			error,
																		) => (
																			<div className='text-red-600 text-start text-[11px]'>
																				{
																					error
																				}
																			</div>
																		),
																	)}
															</div>
														</div>
													) : (
														<input
															value={recEmail}
															onChange={({
																target: {
																	value,
																},
															}) =>
																setRecEmail(
																	value,
																)
															}
															name='recEmail'
															id='recEmail'
															className=' border border-[#E3E3E3] text-gray-700 sm:text-sm   rounded-md focus:ring-primary-600 placeholder:text-[#E3E3E3]  block w-full p-2.5'
															placeholder='E-mail'
															required
														/>
													)}
												</div>
												{error && (
													<div className='text-red-500 scale-[90%] font-medium text-[14px] '>
														Este email não está
														cadastrado no nosso
														banco de dados
														<div className='text-red-500 font-normal text-[12px] '>
															Por favor, verifique
															o email e tente
															novamente.
														</div>
													</div>
												)}
												<div className='relative'>
													<div
														className={`w-full flex  ${
															recStatus ===
															'static'
																? 'justify-center'
																: 'justify-start'
														}`}>
														<button
															type='button'
															disabled={
																buttonLoading
															}
															onClick={
																recStatus ===
																'static'
																	? handleSendEmail
																	: handleNewPassword
															}
															className={`text-white enabled: enabled:hover:bg-[#1c4266] enabled:bg-[#004A80] focus:ring-4 bg-[#004b80d7]  focus:ring-blue-300 font-normal text-[15px] rounded-md flex justify-center  py-2.5  mb-10 mt-2 focus:outline-none 
													${recStatus === 'static' ? 'w-[45%] px-5' : 'px-2 w-[56%] sm:w-[45%] '}
														
														`}>
															{recStatus ===
															'static' ? (
																buttonLoading ? (
																	<svg
																		aria-hidden='true'
																		className='w-7 h-7 text-gray-200 animate-spin   fill-blue-600'
																		viewBox='0 0 100 100'
																		fill='none'
																		xmlns='http://www.w3.org/2000/svg'>
																		<path
																			d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
																			fill='currentColor'
																		/>
																		<path
																			d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
																			fill='currentFill'
																		/>
																	</svg>
																) : (
																	'Enviar'
																)
															) : (
																'Criar nova senha'
															)}
														</button>
													</div>

													<div className='flex items-center  w-full mt-5 justify-between'>
														<button
															type='button'
															onClick={() => {
																setLoading(
																	true,
																);
																setTimeout(
																	() => {
																		setLoading(
																			false,
																		);
																	},
																	1000,
																);
																setRecPage(
																	false,
																);
															}}
															className='text-sm font-medium text-[#9e9e9e] hover:underline font-inter flex'>
															<div className='h-full mr-1 my-auto'>
																<IoChevronBackOutline className='scale-[140%]' />
															</div>

															{`Voltar`}
														</button>
														{recStatus ===
															'Email enviado!' && (
															<button
																type='button'
																onClick={() => {
																	setLoading(
																		true,
																	);
																	setTimeout(
																		() => {
																			setLoading(
																				false,
																			);
																		},
																		1000,
																	);
																	setRecStatus(
																		'static',
																	);
																}}
																className='text-sm font-medium hover:text-[#00C0F3] text-[#616161] '>
																{`reenviar email`}
															</button>
														)}
													</div>
												</div>
											</form>
										</div>
									</div>
								</>
							)}
						</div>
					</section>
				)}
			</div>
		</div>
	);
}

export default Login;
